import { useCallback, useEffect, useRef, useState } from "react";

import { trackNonOpencodeScaffoldError } from "@v2/infra/scaffoldSentry";
import { daytonaClient } from "@v2/trpc/routes/daytona";
import shouldShowV2Debuggers from "@v2/utils/shouldShowV2Debuggers";
import { stripAnsiCodes } from "replo-utils/lib/terminal";
import { z } from "zod";

import { useDaytona } from "./daytona/useDaytona";
import { normalizeRoute } from "./utils";

export type LogMessage = {
  date: Date;
  message: string;
};

const sandboxClientSideErrorInfoSchema = z.object({
  message: z.string(),
  stack: z.string().optional(),
  componentStack: z.string().optional(),
});

type SandboxClientSideErrorInfo = z.infer<
  typeof sandboxClientSideErrorInfoSchema
>;
type SandboxClientSideErrorMap = Map<string, SandboxClientSideErrorInfo>;

const sandboxClientErrorMessageSchema = z.object({
  type: z.literal("replo-editor:client-error"),
  payload: z.object({
    pageRoute: z.string(),
    error: sandboxClientSideErrorInfoSchema,
  }),
});

type SandboxClientErrorMessage = z.infer<
  typeof sandboxClientErrorMessageSchema
>;

function isClientErrorMessage(
  data: unknown,
): data is SandboxClientErrorMessage {
  return sandboxClientErrorMessageSchema.safeParse(data).success;
}

type UseNextDevServerReturn = {
  logs: LogMessage[];
  url: string | undefined;
  /**
   * The route the iframe has confirmed loaded via IFRAME_READY postMessage.
   * Only updated when IFRAME_READY fires — never explicitly cleared.
   */
  loadedRoute: string | null;
  /**
   * Error reported by the iframe for a specific route. "pageNotFound" means
   * turbopack hasn't compiled the page yet (retry). "pageLoadError" means a
   * build/render error (show fix-it UI, don't retry).
   */
  iframeError: {
    route: string;
    status: "pageNotFound" | "pageLoadError";
  } | null;
  /**
   * A key that is incremented when fast refresh performs a full reload. This
   * can be used to force a reload of the iframe.
   */
  refreshEpoch: number;
  error: unknown | null;
  incrementRefreshEpoch: () => void;
  isServerSandboxLoading: boolean;
  isSyncPending: boolean;
  sandboxClientSideErrors: SandboxClientSideErrorMap;
  clearSandboxClientSideError: (pageRoute: string) => void;
  processLogMessage: (message: string) => void;
};

type UseNextDevServerParams = {
  projectId: string | undefined;
  hostingType?: "replo" | "shopify";
};

export function useNextDevServerHook({
  projectId,
  hostingType,
}: UseNextDevServerParams): UseNextDevServerReturn {
  const [loadedRoute, setLoadedRoute] = useState<string | null>(null);

  const [iframeError, setIframeError] = useState<{
    route: string;
    status: "pageNotFound" | "pageLoadError";
  } | null>(null);

  const [logs, setLogs] = useState<LogMessage[]>([]);

  // NOTE (Gabe 2025-08-12): Sometimes fast refresh has to perform a full
  // reload. For some reason, when this happens iframes displaying the content
  // don't reload. We can force them to reload by incrementing this key.
  const [refreshEpoch, setRefreshEpoch] = useState(0);

  // Note (Nick, 2026-01-26, REPL-22167): Track client-side React Runtime errors
  const [sandboxClientSideErrors, setSandboxClientSideErrors] =
    useState<SandboxClientSideErrorMap>(() => new Map());

  // NOTE (Gabe 2025-08-13): This is a ref because processLogMessage needs to
  // use it, and it not being a ref would introduce a cyclical dependency.
  const restartDevServerRef = useRef<(() => Promise<void>) | null>(null);

  const processLogMessage = useCallback((message: string) => {
    const cleanedMessage = stripAnsiCodes(message);

    if (!cleanedMessage) {
      return;
    }

    // NOTE (Noah, 2026-02-20): Log dev server output to console for debugging in PostHog sessions
    // biome-ignore lint/suspicious/noConsole: Intentional - logs captured by PostHog for debugging sandbox issues
    console.log("[next-dev-server]", cleanedMessage);

    // WebContainer
    // #region Error cases
    if (
      cleanedMessage.startsWith("⚠ Fast Refresh had to perform a full reload")
    ) {
      // NOTE (Nick, 2026-02-19): DEBUG — Log when Fast Refresh triggers a full reload (incrementing refreshEpoch but NOT clearing loadedRoute)
      setLogs((prev) => [
        ...prev,
        {
          date: new Date(),
          message: `[debug] ⚠ Fast Refresh full reload detected → incrementing refreshEpoch`,
        },
      ]);
      setRefreshEpoch((k) => k + 1);
    }

    if (
      cleanedMessage.includes(
        "Detected change, manual restart required due to '__NEXT_DISABLE_MEMORY_WATCHER' usage",
      )
    ) {
      void restartDevServerRef.current?.();
    }

    // COMMON

    // #endregion

    setLogs((prev) => [...prev, { date: new Date(), message: cleanedMessage }]);
  }, []);

  // NOTE (Nick, 2026-02-19): DEBUG — Track mount/unmount to detect provider remounts
  useEffect(() => {
    processLogMessage("[debug] useNextDevServerHook MOUNTED");
    return () => {
      // NOTE (Nick, 2026-02-19): setState is silently dropped during React 18
      // unmount cleanup, so we fire-and-forget to the server instead.
      if (projectId) {
        void daytonaClient.logClientEvent.mutate({
          projectId,
          message: "[debug] useNextDevServerHook UNMOUNTED",
        });
      }
    };
  }, [processLogMessage, projectId]);

  // NOTE (Nick, 2026-02-19): DEBUG — Track loadedRoute changes
  useEffect(() => {
    processLogMessage(
      `[debug] loadedRoute changed: ${loadedRoute === null ? "null" : `"${loadedRoute}"`}`,
    );
  }, [loadedRoute, processLogMessage]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "NEXTJS_DEV_INDICATOR_OVERRIDE_REQUEST") {
        event.source?.postMessage(
          {
            type: "NEXTJS_DEV_INDICATOR_OVERRIDE_RESPONSE",
            value: shouldShowV2Debuggers(),
          },
          { targetOrigin: event.origin || "*" },
        );
      }

      // NOTE (Nick, 2026-02-02): Handle IFRAME_READY from fiber-listener.ts —
      // the iframe has successfully loaded a page.
      if (
        event.data?.type === "IFRAME_READY" &&
        event.data?.payload?.pageRoute
      ) {
        const route = normalizeRoute(event.data.payload.pageRoute);
        processLogMessage(
          `[dev-server] IFRAME_READY → loadedRoute = "${route}"`,
        );

        setLoadedRoute(route);
        setIframeError(null);
      }

      // NOTE (Nick, 2026-02-02): Handle PAGE_STATUS_404 from not-found.tsx —
      // turbopack hasn't compiled the page yet. usePage will retry.
      if (
        event.data?.type === "PAGE_STATUS_404" &&
        event.data?.payload?.pageRoute
      ) {
        const route = normalizeRoute(event.data.payload.pageRoute);
        processLogMessage(
          `[dev-server] PAGE_STATUS_404 → iframeError = { route: "${route}", status: 404 }`,
        );
        setIframeError({ route, status: "pageNotFound" });
      }

      // NOTE (Nick, 2026-02-02): Handle PAGE_STATUS_ERROR from error.tsx —
      // a build/render error (e.g. module-not-found). Show fix-it UI, don't retry.
      if (
        event.data?.type === "PAGE_STATUS_ERROR" &&
        event.data?.payload?.pageRoute
      ) {
        const route = normalizeRoute(event.data.payload.pageRoute);
        processLogMessage(
          `[dev-server] PAGE_STATUS_ERROR → iframeError = { route: "${route}", status: 500 }`,
        );
        setIframeError({ route, status: "pageLoadError" });
      }

      // Note (Nick, 2026-01-26, REPL-22167): Handle client-side React Runtime errors from the preview iframe
      if (isClientErrorMessage(event.data)) {
        const { pageRoute, error: errorInfo } = event.data.payload;
        const normalized = normalizeRoute(pageRoute);
        setSandboxClientSideErrors((prev) => {
          const next = new Map(prev);
          next.set(normalized, errorInfo);
          return next;
        });

        // Note (Nick, 2026-02-23, REPL-23283): Send non-opencode scaffold
        // client-side errors to the dedicated Sentry project
        const sentryError = new Error(errorInfo.message);
        if (errorInfo.stack) {
          sentryError.stack = errorInfo.stack;
        }
        trackNonOpencodeScaffoldError(sentryError, {
          pageRoute: normalized,
          stack: errorInfo.stack,
          componentStack: errorInfo.componentStack,
        });
      }
    };
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [processLogMessage]);

  const incrementRefreshEpoch = useCallback(() => {
    setRefreshEpoch((k) => k + 1);
    // Note (Nick, 2026-01-28, REPL-22167): Clear all client-side errors when a new page request cycle starts
    setSandboxClientSideErrors(new Map());
    // NOTE (Nick, 2026-02-03): Clear loadedRoute so isLoading becomes true and
    // the loading bar shows. Any time we force an iframe remount the previous
    // loaded route is no longer valid (e.g. sandbox woke from hibernation with
    // wasRestarted: true but the same URL).
    setLoadedRoute(null);
    setIframeError(null);
  }, []);

  const clearSandboxClientSideError = useCallback((pageRoute: string) => {
    const normalized = normalizeRoute(pageRoute);
    setSandboxClientSideErrors((prev) => {
      const next = new Map(prev);
      next.delete(normalized);
      return next;
    });
  }, []);

  const daytonaResult = useDaytona({
    // Note (Noah, 2025-09-12): Skip fetching daytona if (1) we don't have the flag enabled or (2) we're a v1 project
    forceSkip: hostingType !== "replo",
    projectId,
    processLogMessage,
    incrementRefreshEpoch,
  });

  const serverSandboxUrl = daytonaResult.url;
  const serverSandboxError = daytonaResult.error;
  const isServerSandboxLoading = daytonaResult.isLoading;
  const isSyncPending = daytonaResult.isSyncPending;

  restartDevServerRef.current = daytonaResult.restartDevServer;

  // NOTE (Nick, 2026-02-02): When the sandbox URL changes to a genuinely
  // different value (e.g. sandbox destroyed and recreated), remount the iframe.
  const previousSandboxUrlRef = useRef(serverSandboxUrl);
  useEffect(() => {
    if (
      // NOTE (Nick, 2026-02-02): Use loose equality (!=) to exclude both null
      // and undefined, since the ref starts as undefined on initial page load
      previousSandboxUrlRef.current != null &&
      previousSandboxUrlRef.current !== serverSandboxUrl
    ) {
      processLogMessage(
        `[dev-server] Sandbox URL changed: "${previousSandboxUrlRef.current}" → "${serverSandboxUrl ?? "undefined"}" — clearing loadedRoute`,
      );
      setStuckRetryCount(0);
      incrementRefreshEpoch();
    }
    previousSandboxUrlRef.current = serverSandboxUrl;
  }, [serverSandboxUrl, incrementRefreshEpoch, processLogMessage]);

  // NOTE (Nick, 2026-02-20, REPL-24207):  detect when the sandbox URL
  // is available but the page hasn't loaded (loadedRoute is null) for longer
  // than STUCK_THRESHOLD_MS. Forces an iframe refresh via incrementRefreshEpoch
  // to cover our bases if the page doesn't get loaded
  const STUCK_THRESHOLD_MS = 15_000;
  const MAX_STUCK_RETRIES = 3;
  const [stuckRetryCount, setStuckRetryCount] = useState(0);

  useEffect(() => {
    if (loadedRoute !== null) {
      setStuckRetryCount(0);
    }

    if (!serverSandboxUrl || loadedRoute !== null) {
      return;
    }

    if (stuckRetryCount >= MAX_STUCK_RETRIES) {
      processLogMessage(
        `[watchdog] Page stuck loading — max retries (${MAX_STUCK_RETRIES}) reached`,
      );
      return;
    }

    const timeout = setTimeout(() => {
      setStuckRetryCount((prev) => prev + 1);
      processLogMessage(
        `[watchdog] Page stuck loading for ${STUCK_THRESHOLD_MS / 1000}s ` +
          `(attempt ${stuckRetryCount + 1}/${MAX_STUCK_RETRIES}) — forcing iframe refresh`,
      );
      incrementRefreshEpoch();
    }, STUCK_THRESHOLD_MS);

    return () => clearTimeout(timeout);
  }, [
    serverSandboxUrl,
    loadedRoute,
    stuckRetryCount,
    incrementRefreshEpoch,
    processLogMessage,
  ]);

  return {
    logs,
    refreshEpoch,
    url: serverSandboxUrl,
    loadedRoute,
    iframeError,
    error: serverSandboxError,
    incrementRefreshEpoch,
    isServerSandboxLoading,
    isSyncPending,
    sandboxClientSideErrors,
    clearSandboxClientSideError,
    processLogMessage,
  };
}
